import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from '../../hooks/useLanguage';
import { useUpdateProfileMutation } from '../../store/api/userApi';
import { User } from '../../types/api';
import { ButtonSpinner } from '../ui/Loader';

interface ProfileEditFormProps {
  user: User & {
    referralCount?: number;
    apiKeysCount?: number;
  };
  onSuccess?: () => void;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
}

export function ProfileEditForm({ user, onSuccess }: ProfileEditFormProps) {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [updateProfile, { isLoading, error }] = useUpdateProfileMutation();

  const [formData, setFormData] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    language: user.language || currentLanguage,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
  });

  // Validation functions
  const validateFirstName = (firstName: string): string | undefined => {
    if (!firstName.trim()) {
      return t('validation.firstName.required');
    }
    if (firstName.trim().length < 1) {
      return t('validation.firstName.minLength');
    }
    if (firstName.trim().length > 255) {
      return t('validation.firstName.maxLength');
    }
    return undefined;
  };

  const validateLastName = (lastName: string): string | undefined => {
    if (lastName && lastName.trim().length > 255) {
      return t('validation.lastName.maxLength');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    const firstNameError = validateFirstName(formData.firstName);
    if (firstNameError) newErrors.firstName = firstNameError;
    
    const lastNameError = validateLastName(formData.lastName || '');
    if (lastNameError) newErrors.lastName = lastNameError;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    
    // Mark all fields as touched
    setTouched({ firstName: true, lastName: true });
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      const updateData: {
        firstName?: string;
        lastName?: string | null;
        language?: string;
      } = {};

      // Only include changed fields
      if (formData.firstName !== user.firstName) {
        updateData.firstName = formData.firstName.trim();
      }
      if (formData.lastName !== (user.lastName || '')) {
        updateData.lastName = formData.lastName.trim() || null;
      }
      if (formData.language !== (user.language || currentLanguage)) {
        updateData.language = formData.language;
      }

      // Check if there are any changes
      if (Object.keys(updateData).length === 0) {
        setLocalError(t('profile.edit.noChanges'));
        return;
      }

      await updateProfile(updateData).unwrap();

      // Update language if changed
      if (updateData.language && updateData.language !== currentLanguage) {
        changeLanguage(updateData.language);
      }

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setLocalError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
    setLocalError(null);
  };

  const handleBlur = (field: 'firstName' | 'lastName') => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    if (field === 'firstName') {
      const error = validateFirstName(formData.firstName);
      setErrors((prev) => ({ ...prev, firstName: error }));
    } else if (field === 'lastName') {
      const error = validateLastName(formData.lastName || '');
      setErrors((prev) => ({ ...prev, lastName: error }));
    }
  };

  // Update form when user prop changes
  useEffect(() => {
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      language: user.language || currentLanguage,
    });
  }, [user, currentLanguage]);

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6" noValidate>
      {/* First Name */}
      <div>
        <label
          htmlFor="firstName"
          className="block text-sm font-medium text-neutral-light mb-2 font-body"
        >
          {t('profile.edit.firstName')}
        </label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          onBlur={() => handleBlur('firstName')}
          className={`w-full px-4 py-3 bg-neutral-dark/50 backdrop-blur-sm border rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body ${
            errors.firstName && touched.firstName
              ? 'border-danger'
              : 'border-neutral-dark'
          }`}
          placeholder={t('profile.edit.firstNamePlaceholder')}
        />
        {errors.firstName && touched.firstName && (
          <p className="mt-1 text-sm text-danger font-body animate-slide-down">
            {errors.firstName}
          </p>
        )}
      </div>

      {/* Last Name */}
      <div>
        <label
          htmlFor="lastName"
          className="block text-sm font-medium text-neutral-light mb-2 font-body"
        >
          {t('profile.edit.lastName')} <span className="text-neutral-medium text-xs">({t('common.optional')})</span>
        </label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          onBlur={() => handleBlur('lastName')}
          className={`w-full px-4 py-3 bg-neutral-dark/50 backdrop-blur-sm border rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body ${
            errors.lastName && touched.lastName
              ? 'border-danger'
              : 'border-neutral-dark'
          }`}
          placeholder={t('profile.edit.lastNamePlaceholder')}
        />
        {errors.lastName && touched.lastName && (
          <p className="mt-1 text-sm text-danger font-body animate-slide-down">
            {errors.lastName}
          </p>
        )}
      </div>

      {/* Language */}
      <div>
        <label
          htmlFor="language"
          className="block text-sm font-medium text-neutral-light mb-2 font-body"
        >
          {t('profile.edit.language')}
        </label>
        <select
          id="language"
          name="language"
          value={formData.language}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-neutral-dark/50 backdrop-blur-sm border border-neutral-dark rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body"
        >
          <option value="ar">{t('profile.edit.languageAr')}</option>
          <option value="en">{t('profile.edit.languageEn')}</option>
          <option value="zh">{t('profile.edit.languageZh')}</option>
          <option value="hi">{t('profile.edit.languageHi')}</option>
          <option value="ha">{t('profile.edit.languageHa')}</option>
          <option value="ru">{t('profile.edit.languageRu')}</option>
          <option value="es">{t('profile.edit.languageEs')}</option>
          <option value="tr">{t('profile.edit.languageTr')}</option>
          <option value="fr">{t('profile.edit.languageFr')}</option>
        </select>
      </div>

      {/* Error Message */}
      {localError && (
        <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
          <p className="text-sm text-danger font-body">{localError}</p>
        </div>
      )}

      {/* API Error */}
      {error && 'data' in error && (
        <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
          <p className="text-sm text-danger font-body">
            {(error.data as { message?: string })?.message || t('errors.network')}
          </p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <ButtonSpinner />
            <span>{t('common.loading')}</span>
          </>
        ) : (
          t('common.save')
        )}
      </button>
    </form>
  );
}

